/**
 * Generic hook for executing Epicor tools
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import * as api from '../lib/api'

/**
 * Hook for executing a single tool call
 */
export function useTool(toolName, defaultParams = {}) {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastFetched, setLastFetched] = useState(null)
  const abortControllerRef = useRef(null)

  /**
   * Execute the tool with optional parameters
   */
  const execute = useCallback(async (params = {}) => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setIsLoading(true)
    setError(null)

    try {
      const result = await api.executeTool(toolName, { ...defaultParams, ...params })
      setData(result)
      setLastFetched(new Date())
      return result
    } catch (err) {
      if (err.name === 'AbortError') {
        return null
      }
      setError(err.message || 'An error occurred')
      throw err
    } finally {
      setIsLoading(false)
      abortControllerRef.current = null
    }
  }, [toolName, defaultParams])

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setData(null)
    setError(null)
    setLastFetched(null)
  }, [])

  /**
   * Cancel ongoing request
   */
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      setIsLoading(false)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    data,
    isLoading,
    error,
    lastFetched,
    execute,
    reset,
    cancel,
  }
}

/**
 * Hook for auto-fetching tool data on mount
 */
export function useToolData(toolName, params = {}, options = {}) {
  const { autoFetch = true, refreshInterval = null } = options
  const tool = useTool(toolName, params)

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      tool.execute()
    }
  }, [autoFetch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Set up refresh interval if specified
  useEffect(() => {
    if (!refreshInterval) return

    const interval = setInterval(() => {
      tool.execute()
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [refreshInterval]) // eslint-disable-line react-hooks/exhaustive-deps

  return tool
}

/**
 * Hook for dashboard data - fetches multiple tools at once
 */
export function useDashboardData() {
  const [data, setData] = useState({
    cashPosition: null,
    arAging: null,
    apAging: null,
    jobOverruns: null,
    lateJobs: null,
    expiringQuotes: null,
    creditHolds: null,
    productionCapacity: null,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [errors, setErrors] = useState({})

  const fetchAll = useCallback(async () => {
    setIsLoading(true)
    setErrors({})

    const results = await Promise.allSettled([
      api.getDailyCashPosition(),
      api.getARSummary(),
      api.getAPSummary(),
      api.checkJobOverruns(),
      api.checkLateJobs(),
      api.checkExpiringQuotes(),
      api.checkCreditHolds(),
      api.getProductionCapacity(),
    ])

    const keys = [
      'cashPosition',
      'arAging',
      'apAging',
      'jobOverruns',
      'lateJobs',
      'expiringQuotes',
      'creditHolds',
      'productionCapacity',
    ]

    const newData = {}
    const newErrors = {}

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        newData[keys[index]] = result.value
      } else {
        newErrors[keys[index]] = result.reason?.message || 'Failed to load'
        newData[keys[index]] = null
      }
    })

    setData(prev => ({ ...prev, ...newData }))
    setErrors(newErrors)
    setIsLoading(false)
  }, [])

  // Fetch on mount
  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  return {
    ...data,
    isLoading,
    errors,
    refresh: fetchAll,
  }
}

/**
 * Hook for alerts data
 */
export function useAlerts() {
  const [alerts, setAlerts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAlerts = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const results = await Promise.allSettled([
        api.checkJobOverruns(),
        api.checkLateJobs(),
        api.checkExpiringQuotes(),
        api.checkCreditHolds(),
        api.checkARAlerts(),
        api.checkAPAlerts(),
      ])

      const allAlerts = []

      // Process job overruns
      if (results[0].status === 'fulfilled' && results[0].value?.jobs) {
        results[0].value.jobs.forEach(job => {
          allAlerts.push({
            id: `overrun_${job.job_num}`,
            type: 'job_overrun',
            severity: 'warning',
            title: `Job ${job.job_num} Over Budget`,
            description: `${job.variance_pct}% over budget`,
            data: job,
            timestamp: new Date().toISOString(),
          })
        })
      }

      // Process late jobs
      if (results[1].status === 'fulfilled' && results[1].value?.jobs) {
        results[1].value.jobs.forEach(job => {
          allAlerts.push({
            id: `late_${job.job_num}`,
            type: 'late_job',
            severity: 'error',
            title: `Job ${job.job_num} is Late`,
            description: `Due ${job.due_date}, ${job.days_late} days late`,
            data: job,
            timestamp: new Date().toISOString(),
          })
        })
      }

      // Process expiring quotes
      if (results[2].status === 'fulfilled' && results[2].value?.quotes) {
        results[2].value.quotes.forEach(quote => {
          allAlerts.push({
            id: `quote_${quote.quote_num}`,
            type: 'expiring_quote',
            severity: 'info',
            title: `Quote ${quote.quote_num} Expiring`,
            description: `Expires ${quote.expire_date}`,
            data: quote,
            timestamp: new Date().toISOString(),
          })
        })
      }

      // Process credit holds
      if (results[3].status === 'fulfilled' && results[3].value?.customers) {
        results[3].value.customers.forEach(cust => {
          allAlerts.push({
            id: `credit_${cust.cust_num}`,
            type: 'credit_hold',
            severity: 'error',
            title: `${cust.name} on Credit Hold`,
            description: `Over limit by ${cust.over_limit}`,
            data: cust,
            timestamp: new Date().toISOString(),
          })
        })
      }

      // Process AR alerts
      if (results[4].status === 'fulfilled' && results[4].value?.alerts) {
        results[4].value.alerts.forEach(alert => {
          allAlerts.push({
            id: `ar_${alert.invoice_num}`,
            type: 'ar_overdue',
            severity: 'warning',
            title: `Invoice ${alert.invoice_num} Overdue`,
            description: alert.message,
            data: alert,
            timestamp: new Date().toISOString(),
          })
        })
      }

      // Process AP alerts
      if (results[5].status === 'fulfilled' && results[5].value?.alerts) {
        results[5].value.alerts.forEach(alert => {
          allAlerts.push({
            id: `ap_${alert.invoice_num}`,
            type: 'ap_due',
            severity: 'info',
            title: `AP Invoice Due Soon`,
            description: alert.message,
            data: alert,
            timestamp: new Date().toISOString(),
          })
        })
      }

      // Sort by severity then timestamp
      const severityOrder = { error: 0, warning: 1, info: 2 }
      allAlerts.sort((a, b) => {
        const sevDiff = severityOrder[a.severity] - severityOrder[b.severity]
        if (sevDiff !== 0) return sevDiff
        return new Date(b.timestamp) - new Date(a.timestamp)
      })

      setAlerts(allAlerts)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAlerts()
    // Refresh every 5 minutes
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [fetchAlerts])

  return {
    alerts,
    isLoading,
    error,
    refresh: fetchAlerts,
    alertCount: alerts.length,
    errorCount: alerts.filter(a => a.severity === 'error').length,
    warningCount: alerts.filter(a => a.severity === 'warning').length,
  }
}

/**
 * Simple hook that exposes execute function for tools
 * Used by pages for straightforward tool execution
 */
export function useTools() {
  const [loading, setLoading] = useState(false)

  const execute = useCallback(async (toolName, params = {}) => {
    setLoading(true)
    try {
      const result = await api.executeTool(toolName, params)
      return result
    } catch (err) {
      console.error(`Tool ${toolName} failed:`, err)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  return { execute, loading }
}

export default useTool
